/**
 * DatasetDetailsPanel Component
 *
 * An inline side panel that displays the full details of a selected dataset.
 * Uses the generic DetailsSidePanel for layout and close-button behavior.
 * Includes a notes section for viewing and adding notes.
 */

import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { File, Copy, Check, Pencil, Download, Loader2, RotateCw } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Dataset, DatasetNote, prettifyDomainValue, addNote, downloadFile, PERMITTED_USERS_ALL_SENTINEL, isSharedWithUser } from "@/services/datasetsService"
import DetailsSidePanel from "@/components/shared/DetailsSidePanel"
import { useState, useMemo, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

/**
 * Custom icon: a monospace "R" inside a filled circle, used as the button icon
 * for the R snippet feature. The circle is filled with currentColor and the R
 * is rendered in white.
 */
function RCircleIcon({ className }: { className?: string }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <circle cx="12" cy="12" r="11.5" fill="#86b9e3" />
      <text
        x="12"
        y="17"
        textAnchor="middle"
        fontSize="15"
        fontFamily="monospace"
        fontWeight="bold"
        fill="white"
      >
        R
      </text>
    </svg>
  )
}

/** Props for the DatasetDetailsPanel component. */
interface DatasetDetailsPanelProps {
  /** The dataset to display. */
  dataset: Dataset
  /** Map of user sub -> display name for owner rendering. */
  userNameMap: Map<string, string>
  /** Map of user sub -> active status. False means the user was removed from Auth0. */
  userActiveMap: Map<string, boolean>
  /** Current user's Auth0 sub claim (for highlighting ownership). */
  currentUserSub: string
  /** Callback to close the panel. */
  onClose: () => void
  /** Callback fired after a note is successfully added, to refresh dataset data. */
  onNoteAdded?: () => void
}

/**
 * Format an ISO date string to a human-readable format.
 *
 * @param isoDate - ISO 8601 date string.
 * @returns Formatted date string (e.g., "15 Oct 2024 at 14:30").
 */
function formatDateTime(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return isoDate
  }
}

/**
 * Format an ISO date string to a human-readable date (date only, no time).
 *
 * @param isoDate - ISO 8601 date string.
 * @returns Formatted date string (e.g., "15 Oct 2024").
 */
function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate)
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  } catch {
    return isoDate
  }
}

/**
 * Inline side panel displaying full details of a dataset.
 *
 * Renders all available fields for the selected dataset in a scrollable
 * panel that sits alongside the main content.
 *
 * @param props - Component props.
 * @param props.dataset - The selected dataset to display.
 * @param props.userNameMap - Map of user sub to display name.
 * @param props.userActiveMap - Map of user sub to active status.
 * @param props.currentUserSub - Current user's sub for ownership indicator.
 * @param props.onClose - Callback to close the panel.
 */
export default function DatasetDetailsPanel({
  dataset,
  userNameMap,
  userActiveMap,
  currentUserSub,
  onClose,
  onNoteAdded,
}: DatasetDetailsPanelProps): React.JSX.Element {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()
  const [copiedId, setCopiedId] = useState<boolean>(false)
  const [noteText, setNoteText] = useState<string>("")
  const [isSubmittingNote, setIsSubmittingNote] = useState<boolean>(false)
  const [noteError, setNoteError] = useState<string | null>(null)
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  // State for the download restrictions confirmation dialog.
  // When a user clicks download on a restricted dataset, we store the filename
  // here to show the dialog. On confirm, the download proceeds.
  const [pendingDownloadFile, setPendingDownloadFile] = useState<string | null>(null)

  // State for the R snippet dialog. When set, the dialog is open showing
  // the snippet for the specified file.
  const [snippetFile, setSnippetFile] = useState<string | null>(null)
  const [snippetCopied, setSnippetCopied] = useState<boolean>(false)

  // State for the selected version in the files section. Defaults to the
  // latest version (currentVersion) from the dataset record.
  const [selectedVersion, setSelectedVersion] = useState<number>(dataset.currentVersion)

  const ownerName = userNameMap.get(dataset.owner) ?? dataset.owner
  const isOwnedByMe = dataset.owner === currentUserSub

  /**
   * Whether the dataset has any sharing restrictions that should trigger a
   * confirmation dialog before downloading dataset files. True if any of:
   * - Permitted users is not "All users"
   * - Publishable is "CONFIDENTIAL"
   * - There's a sharing agreement file attached
   * - There's usage permissions text
   */
  const hasRestrictions: boolean = useMemo(() => {
    const permittedUsers = dataset.permittedUsers ?? []
    const isSharedWithAll = permittedUsers.includes(PERMITTED_USERS_ALL_SENTINEL)
    const isConfidential = dataset.publishable === "CONFIDENTIAL"
    const hasSharingAgreementFile = (dataset.sharingAgreementFileKeys ?? []).length > 0
    const hasUsagePermissionsText = !!dataset.sharingAgreementText?.trim()

    return !isSharedWithAll || isConfidential || hasSharingAgreementFile || hasUsagePermissionsText
  }, [dataset])

  /** Check if user can add notes (owner or shared). */
  const canAddNotes: boolean = useMemo(() => {
    return isSharedWithUser(dataset, currentUserSub)
  }, [dataset, currentUserSub])

  /** Check if user can download files (owner or shared). */
  const canDownload: boolean = useMemo(() => {
    return isSharedWithUser(dataset, currentUserSub)
  }, [dataset, currentUserSub])

  /** File keys for the currently selected version. */
  const selectedVersionFileKeys: string[] = useMemo(() => {
    const versionData = dataset.versions[String(selectedVersion)]
    return versionData?.fileKeys ?? []
  }, [dataset.versions, selectedVersion])

  /** List of all available version numbers (ascending). */
  const versionNumbers: number[] = useMemo(() => {
    return Object.keys(dataset.versions)
      .map(Number)
      .sort((a, b) => a - b)
  }, [dataset.versions])

  /** Notes sorted chronologically (oldest first, newest at bottom). */
  const sortedNotes: DatasetNote[] = useMemo(() => {
    const notes = dataset.notes ?? []
    return [...notes].sort((a, b) => {
      return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
    })
  }, [dataset.notes])

  /**
   * Copy the dataset ID to clipboard.
   */
  const handleCopyId = async (): Promise<void> => {
    await navigator.clipboard.writeText(dataset.datasetId)
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 2000)
  }

  /**
   * Navigate to the edit page for this dataset.
   */
  const handleEdit = (): void => {
    navigate(`/datasets/edit/${dataset.datasetId}`)
  }

  /**
   * Handle note submission.
   */
  const handleAddNote = useCallback(async (): Promise<void> => {
    const trimmedNote = noteText.trim()
    if (!trimmedNote) return

    setIsSubmittingNote(true)
    setNoteError(null)

    try {
      const token = await getAccessToken()
      await addNote(token, dataset.datasetId, trimmedNote)
      setNoteText("")
      // Notify parent to refresh dataset data
      onNoteAdded?.()
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : "Failed to add note")
    } finally {
      setIsSubmittingNote(false)
    }
  }, [noteText, getAccessToken, dataset.datasetId, onNoteAdded])

  /**
   * Execute the actual file download (called directly or after dialog confirm).
   *
   * @param filename - The file key to download.
   */
  const executeDownload = useCallback(async (filename: string): Promise<void> => {
    setDownloadingFile(filename)
    setDownloadError(null)

    try {
      const token = await getAccessToken()
      await downloadFile(token, dataset.datasetId, filename, selectedVersion)
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Failed to download file")
    } finally {
      setDownloadingFile(null)
    }
  }, [getAccessToken, dataset.datasetId, selectedVersion])

  /**
   * Handle file download. If the dataset has restrictions, shows a
   * confirmation dialog first. Otherwise downloads immediately.
   *
   * @param filename - The file key to download.
   */
  const handleDownload = useCallback(async (filename: string): Promise<void> => {
    if (hasRestrictions) {
      // Show the confirmation dialog; actual download happens on confirm.
      setPendingDownloadFile(filename)
      return
    }
    await executeDownload(filename)
  }, [hasRestrictions, executeDownload])

  /**
   * Handle confirmation from the restrictions dialog — proceed with download.
   */
  const handleConfirmDownload = useCallback(async (): Promise<void> => {
    const filename = pendingDownloadFile
    setPendingDownloadFile(null)
    if (filename) {
      await executeDownload(filename)
    }
  }, [pendingDownloadFile, executeDownload])

  /**
   * Copy the R snippet for a given file to the clipboard.
   *
   * @param filename - The file key to generate the snippet for.
   */
  const handleCopySnippet = useCallback(async (filename: string): Promise<void> => {
    const fileRef = `datasets://${dataset.datasetId}/${selectedVersion}/${filename}`
    const snippet = `wasp::ds_download("${fileRef}", destfile=)`
    await navigator.clipboard.writeText(snippet)
    setSnippetCopied(true)
    setTimeout(() => setSnippetCopied(false), 2000)
  }, [dataset.datasetId, selectedVersion])

  /**
   * Handle sharing agreement file download.
   *
   * Uses the same download endpoint with type=sharing_agreement so the backend
   * checks the sharingAgreementFileKeys list and resolves the correct S3 prefix.
   * The version parameter is required by the API contract but is not used for
   * sharing agreement path resolution (they are not versioned).
   *
   * @param filename - The sharing agreement file key to download.
   */
  const handleDownloadSharingAgreement = useCallback(async (filename: string): Promise<void> => {
    setDownloadingFile(`sa:${filename}`)
    setDownloadError(null)

    try {
      const token = await getAccessToken()
      await downloadFile(token, dataset.datasetId, filename, selectedVersion, "sharing_agreement")
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Failed to download sharing agreement")
    } finally {
      setDownloadingFile(null)
    }
  }, [getAccessToken, dataset.datasetId, selectedVersion])

  return (
    <>
      <DetailsSidePanel
        onClose={onClose}
        header={
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-sm leading-snug line-clamp-2">{dataset.title}</h3>
            <div className="flex items-center gap-1 mt-1">
              <p className="text-xs text-muted-foreground truncate">
                ID: {dataset.datasetId.slice(0, 8)}…
              </p>
              <Button
                variant="ghost"
                size="icon"
                className="size-5"
                onClick={handleCopyId}
                title="Copy full ID"
              >
                {copiedId ? (
                  <Check className="size-3 text-green-600" />
                ) : (
                  <Copy className="size-3" />
                )}
              </Button>
            </div>
          </div>
        }
        headerActions={
          isOwnedByMe ? (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={handleEdit} title="Edit dataset">
                <Pencil className="size-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/datasets/${dataset.datasetId}/new-version`)}
                title="Deprecate files and upload new version"
              >
                <RotateCw className="size-4 mr-1" />
                New Version
              </Button>
            </div>
          ) : undefined
        }
      >
        {/* Scrollable content area */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Description */}
            {dataset.description && (
              <DetailSection label="Description">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{dataset.description}</p>
              </DetailSection>
            )}

            {/* Data Topics */}
            <DetailSection label="Data Topics">
              <div className="flex flex-wrap gap-1">
                {dataset.dataTopic.map(topic => (
                  <Badge key={topic} className="bg-primary/25 text-foreground">
                    {topic}
                  </Badge>
                ))}
              </div>
            </DetailSection>

            {/* Files */}
            <DetailSection label={`Files (${selectedVersionFileKeys.length})`}>
              {/* Version selector */}
              {versionNumbers.length > 1 && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-muted-foreground">Version:</span>
                  <Select
                    value={String(selectedVersion)}
                    onValueChange={(value: string) => setSelectedVersion(Number(value))}
                  >
                    <SelectTrigger className="h-7 w-auto min-w-[4rem] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {versionNumbers.map(v => (
                        <SelectItem key={v} value={String(v)}>
                          {v}{v === dataset.currentVersion ? " (latest)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Deprecation warning for non-latest versions */}
              {selectedVersion !== dataset.currentVersion && (
                <p className="text-xs text-amber-600 mb-2">
                  This is not the latest version.
                  {dataset.versions[String(selectedVersion)]?.deprecationReason && (
                    <> Reason: {dataset.versions[String(selectedVersion)].deprecationReason}</>
                  )}
                </p>
              )}
              <ul className="space-y-1">
                {selectedVersionFileKeys.map(fileKey => {
                  const isDownloading = downloadingFile === fileKey
                  return (
                    <li key={fileKey} className="flex items-center gap-2 text-sm">
                      <File className="size-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{fileKey}</span>
                      {canDownload && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-6 shrink-0"
                            onClick={() => setSnippetFile(fileKey)}
                            title={`Get R snippet for ${fileKey}`}
                          >
                            <RCircleIcon className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-6 shrink-0"
                            onClick={() => handleDownload(fileKey)}
                            disabled={isDownloading}
                            title={`Download ${fileKey}`}
                          >
                            {isDownloading ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Download className="size-3.5" />
                            )}
                          </Button>
                        </>
                      )}
                    </li>
                  )
                })}
              </ul>
              {downloadError && <p className="text-xs text-destructive mt-2">{downloadError}</p>}
            </DetailSection>

            <Separator />

            {/* ------------------------------------------------------------- */}
            {/* Sharing Permissions                                           */}
            {/* ------------------------------------------------------------- */}

            {/* Sharing info */}
            <DetailSection label="Permitted MAP Users">
              {dataset.permittedUsers && dataset.permittedUsers.length > 0 ? (
                dataset.permittedUsers.includes(PERMITTED_USERS_ALL_SENTINEL) ? (
                  <Badge variant="outline" className="text-xs text-foreground">
                    All users
                  </Badge>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {dataset.permittedUsers.map(sub => {
                      const isMe = sub === currentUserSub
                      const isActive = userActiveMap.get(sub) !== false
                      return (
                        <Badge
                          key={sub}
                          variant={isMe ? "default" : "outline"}
                          className={
                            isMe
                              ? "text-xs text-foreground"
                              : isActive
                                ? "text-xs"
                                : "text-xs text-muted-foreground line-through"
                          }
                        >
                          {isMe
                            ? "You"
                            : (userNameMap.get(sub) ?? sub.slice(0, 12) + "…") +
                              (isActive ? "" : " (removed)")}
                        </Badge>
                      )
                    })}
                  </div>
                )
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailSection>

            {/* Publishable */}
            <div className="grid grid-cols-2 gap-3">
              <DetailSection label="Publishable">
                {dataset.publishable ? (
                  <span>{prettifyDomainValue(dataset.publishable)}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailSection>

              {/* Data Sharing Agreement File */}
              <DetailSection label="Data Sharing Agreement">
                {dataset.sharingAgreementFileKeys && dataset.sharingAgreementFileKeys.length > 0 ? (
                  <ul className="space-y-1">
                    {dataset.sharingAgreementFileKeys.map(fileKey => {
                      const isDownloadingSA = downloadingFile === `sa:${fileKey}`
                      return (
                        <li key={fileKey} className="flex items-center gap-2 text-sm">
                          <File className="size-3.5 text-muted-foreground shrink-0" />
                          <span className="truncate flex-1">{fileKey}</span>
                          {canDownload && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6 shrink-0"
                              onClick={() => handleDownloadSharingAgreement(fileKey)}
                              disabled={isDownloadingSA}
                              title={`Download ${fileKey}`}
                            >
                              {isDownloadingSA ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Download className="size-3.5" />
                              )}
                            </Button>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailSection>
            </div>

            {/* Usage Permissions */}
            <DetailSection label="Usage Permissions">
              {dataset.sharingAgreementText ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {dataset.sharingAgreementText}
                </p>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailSection>

            <Separator />

            {/* ------------------------------------------------------------- */}
            {/* Metadata                                                      */}
            {/* ------------------------------------------------------------- */}

            {/* Unit of Analysis */}
            <DetailSection label="Unit of Analysis">
              {dataset.unitOfAnalysis && dataset.unitOfAnalysis.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {dataset.unitOfAnalysis.map(unit => (
                    <Badge key={unit} variant="secondary">
                      {unit}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailSection>

            {/* Countries */}
            <DetailSection label="Countries">
              {dataset.countries && dataset.countries.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {dataset.countries.map(iso3 => (
                    <Badge key={iso3} variant="outline">
                      {iso3}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </DetailSection>

            {/* Temporal & Spatial */}
            <div className="grid grid-cols-2 gap-3">
              <DetailSection label="Temporal Dimension">
                {dataset.temporalDimension || "—"}
              </DetailSection>
              <DetailSection label="Temporal Resolution">
                {dataset.temporalResolution && dataset.temporalResolution.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {dataset.temporalResolution.map(tr => (
                      <Badge key={tr} variant="outline" className="text-xs">
                        {prettifyDomainValue(tr)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </DetailSection>
              <DetailSection label="Level of Spatial Aggregation">
                {dataset.spatialAggregationLevel && dataset.spatialAggregationLevel.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {dataset.spatialAggregationLevel.map(sal => (
                      <Badge key={sal} variant="outline" className="text-xs">
                        {prettifyDomainValue(sal)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </DetailSection>
            </div>

            <Separator />

            {/* ------------------------------------------------------------- */}
            {/* Ownership & Source                                            */}
            {/* ------------------------------------------------------------- */}

            <div className="grid grid-cols-2 gap-3">
              <DetailSection label="Owner">
                <span className={isOwnedByMe ? "font-medium" : ""}>
                  {isOwnedByMe ? "You" : ownerName}
                </span>
              </DetailSection>
              <DetailSection label="Biblio ID">
                {dataset.biblioId !== undefined ? (
                  <span>{dataset.biblioId}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </DetailSection>
            </div>

            <Separator />

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-3">
              <DetailSection label="Uploaded">{formatDateTime(dataset.uploadDate)}</DetailSection>
              <DetailSection label="Last Modified">
                {formatDateTime(dataset.lastModified)}
              </DetailSection>
            </div>

            <Separator />

            {/* Notes Section */}
            <DetailSection label={`Notes (${sortedNotes.length})`}>
              {sortedNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              ) : (
                <div className="space-y-3 mt-2">
                  {sortedNotes.map((note, index) => {
                    const authorName =
                      userNameMap.get(note.createdBy) ?? note.createdBy.slice(0, 12) + "…"
                    const isMyNote = note.createdBy === currentUserSub
                    return (
                      <div
                        key={`${note.createdBy}-${note.createdOn}-${index}`}
                        className="rounded-md bg-muted/50 border border-border p-3 space-y-2"
                      >
                        <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs text-muted-foreground">
                            {isMyNote ? "You" : authorName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(note.createdOn)}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </DetailSection>

            {/* Add Note Form (only for authorized users) */}
            {canAddNotes && (
              <div className="space-y-2 mt-2">
                <Textarea
                  placeholder="Write a new note…"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  disabled={isSubmittingNote}
                  className="resize-none"
                  rows={3}
                />
                {noteError && <p className="text-xs text-destructive">{noteError}</p>}
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleAddNote}
                    disabled={isSubmittingNote || !noteText.trim()}
                  >
                    {isSubmittingNote ? "Adding…" : "Add Note"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DetailsSidePanel>

      {/* Download restrictions confirmation dialog */}
      <AlertDialog
        open={pendingDownloadFile !== null}
        onOpenChange={open => {
          if (!open) setPendingDownloadFile(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restricted Dataset</AlertDialogTitle>
            <AlertDialogDescription>
              This dataset has usage restrictions. Please review before downloading.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            {/* Permitted Users */}
            {!(dataset.permittedUsers ?? []).includes(PERMITTED_USERS_ALL_SENTINEL) && (
              <div>
                <p className="font-medium text-muted-foreground text-xs mb-0.5">Permitted Users</p>
                <p>This dataset is not shared with all users.</p>
              </div>
            )}
            {/* Publishable */}
            {dataset.publishable === "CONFIDENTIAL" && (
              <div>
                <p className="font-medium text-muted-foreground text-xs mb-0.5">Publishable</p>
                <p>Confidential — not cleared for public release.</p>
              </div>
            )}
            {/* Data Sharing Agreement File */}
            {(dataset.sharingAgreementFileKeys ?? []).length > 0 && (
              <div>
                <p className="font-medium text-muted-foreground text-xs mb-0.5">
                  Data Sharing Agreement
                </p>
                <p>{dataset.sharingAgreementFileKeys!.join(", ")}</p>
              </div>
            )}
            {/* Usage Permissions */}
            {dataset.sharingAgreementText?.trim() && (
              <div>
                <p className="font-medium text-muted-foreground text-xs mb-0.5">
                  Usage Permissions
                </p>
                <p className="whitespace-pre-wrap">{dataset.sharingAgreementText}</p>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDownload}>Download</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* R snippet dialog */}
      <Dialog
        open={snippetFile !== null}
        onOpenChange={open => {
          if (!open) {
            setSnippetFile(null)
            setSnippetCopied(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Load in Workbench</DialogTitle>
            <DialogDescription>
              Use the following R snippet(s) to use this file in R on Workbench.
            </DialogDescription>
          </DialogHeader>

          {/* Restrictions warning (same conditions as download dialog) */}
          {hasRestrictions && (
            <div className="space-y-3 text-sm">
              <p className="text-sm font-semibold">Dataset Restrictions</p>
              {!(dataset.permittedUsers ?? []).includes(PERMITTED_USERS_ALL_SENTINEL) && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs mb-0.5">
                    Permitted Users
                  </p>
                  <p>This dataset is not shared with all users.</p>
                </div>
              )}
              {dataset.publishable === "CONFIDENTIAL" && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs mb-0.5">Publishable</p>
                  <p>Confidential — not cleared for public release.</p>
                </div>
              )}
              {(dataset.sharingAgreementFileKeys ?? []).length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs mb-0.5">
                    Data Sharing Agreement
                  </p>
                  <p>{dataset.sharingAgreementFileKeys!.join(", ")}</p>
                </div>
              )}
              {dataset.sharingAgreementText?.trim() && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs mb-0.5">
                    Usage Permissions
                  </p>
                  <p className="whitespace-pre-wrap">{dataset.sharingAgreementText}</p>
                </div>
              )}
            </div>
          )}

          {/* Code snippets */}
          {snippetFile &&
            (() => {
              const ext = snippetFile.split(".").pop()?.toLowerCase() ?? ""
              const readFuncMap: Record<string, string> = {
                csv: "ds_read_csv",
                rds: "ds_read_rds",
                xlsx: "ds_read_xlsx",
              }
              const readFunc = readFuncMap[ext]
              // Canonical dataset reference URIs. The file form addresses a
              // single file; the version form (no file) addresses a whole
              // version for the download-all case.
              const fileRef = `datasets://${dataset.datasetId}/${selectedVersion}/${snippetFile}`
              const versionRef = `datasets://${dataset.datasetId}/${selectedVersion}`
              const readSnippet = readFunc
                ? `df <- wasp::${readFunc}("${fileRef}")`
                : null
              const downloadSnippet = `wasp::ds_download("${fileRef}", destfile=)`
              const downloadAllSnippet = `wasp::ds_download_all("${versionRef}", destdir=)`

              return (
                <div className="space-y-3">
                  {/* Workbench credentials setup — collapsed by default */}
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      First-time setup: Workbench credentials
                    </summary>
                    <div className="mt-2 space-y-1.5 pl-3">
                      <p className="text-muted-foreground">
                        You need to set up credentials for wasp to use. You must be on the new
                        Workbench page, not the legacy one.
                      </p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Open the Session Launch popup (New Session or Resume Session)</li>
                        <li>Find the Session Credentials section</li>
                        <li>Click the cog icon to the right of Session Credentials</li>
                        <li>Sign in via the Custom OAuth integration</li>
                        <li>Go back to the Session Launch screen and ensure that "posit-workbench-user-&lt;office&gt;" is checked before launching the session</li>
                      </ol>
                    </div>
                  </details>

                  {/* wasp package requirement — collapsed by default */}
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                      Requires the <code className="font-mono text-xs">wasp</code> R package
                    </summary>
                    <div className="mt-2 space-y-1.5 pl-3">
                      <p className="text-muted-foreground">Install or update with:</p>
                      <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                        <code className="text-sm font-mono flex-1 break-all">
                          pak::pak(&quot;gitlab::malaria-atlas-project/data-eng/aws/wasp&quot;)
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              'pak::pak("gitlab::malaria-atlas-project/data-eng/aws/wasp")'
                            )
                            setSnippetCopied(true)
                            setTimeout(() => setSnippetCopied(false), 2000)
                          }}
                          title="Copy install command"
                        >
                          <Copy className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                  </details>

                  {/* Load as data frame — open by default when available */}
                  {readSnippet && (
                    <details open className="text-sm">
                      <summary className="cursor-pointer font-medium hover:text-foreground">
                        Load as data frame
                      </summary>
                      <div className="mt-1.5 flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                        <code className="text-sm font-mono flex-1 break-all">{readSnippet}</code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 shrink-0"
                          onClick={async () => {
                            await navigator.clipboard.writeText(readSnippet)
                            setSnippetCopied(true)
                            setTimeout(() => setSnippetCopied(false), 2000)
                          }}
                          title="Copy snippet"
                        >
                          {snippetCopied ? (
                            <Check className="size-3.5 text-green-600" />
                          ) : (
                            <Copy className="size-3.5" />
                          )}
                        </Button>
                      </div>
                    </details>
                  )}

                  {/* Download single file — open by default only when read is not available */}
                  <details open={!readSnippet} className="text-sm">
                    <summary className="cursor-pointer font-medium hover:text-foreground">
                      Download this file to disk
                    </summary>
                    <div className="mt-1.5 flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                      <code className="text-sm font-mono flex-1 break-all">{downloadSnippet}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0"
                        onClick={() => handleCopySnippet(snippetFile)}
                        title="Copy snippet"
                      >
                        <Copy className="size-3.5" />
                      </Button>
                    </div>
                  </details>

                  {/* Download all files — always collapsed */}
                  <details className="text-sm">
                    <summary className="cursor-pointer font-medium hover:text-foreground">
                      Download all files in this dataset
                    </summary>
                    <div className="mt-1.5 flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
                      <code className="text-sm font-mono flex-1 break-all">
                        {downloadAllSnippet}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0"
                        onClick={async () => {
                          await navigator.clipboard.writeText(downloadAllSnippet)
                          setSnippetCopied(true)
                          setTimeout(() => setSnippetCopied(false), 2000)
                        }}
                        title="Copy snippet"
                      >
                        <Copy className="size-3.5" />
                      </Button>
                    </div>
                  </details>
                </div>
              )
            })()}
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Helper component for rendering a labelled detail field.
 *
 * @param props - Component props.
 * @param props.label - The field label.
 * @param props.children - The field value content.
 */
function DetailSection({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground mb-0.5">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  )
}
